<template>
  <div class="contact-page">
    <div class="hero-section">
      <h1>Get in Touch</h1>
    </div>
    
    <div class="contact-container">
      <div class="form-section">
        <h2 class="decorated-title"><i><b><u>Contact Us</u></b></i></h2>
        <p class="contact-description">
          Have questions about our tours or need help planning your Yunnan adventure? 
          We're here to help! Fill out the form or contact us directly, and our travel experts 
          will get back to you within 24 hours.
        </p>
        
        <div class="contact-item">
          <div class="contact-email">
            <font-awesome-icon icon="envelope" class="icon" />
            <a href="mailto:info@realyunnan.com">info@realyunnan.com</a>
          </div>
        </div>
        
        <div class="form-wrapper">
          <iframe 
            ref="formIframe"
            src="https://docs.google.com/forms/d/e/1FAIpQLSejLKZMT18hwPrg5qKiWACdyoPpXvcDbel_7ZLm4tssNjxTOw/viewform?embedded=true"
            width="100%" 
            height="650" 
            frameborder="0" 
            marginheight="0" 
            marginwidth="0">
            Loading…
          </iframe>
        </div>
      </div>
    </div>
    
    <!-- TestimonialsSection at the bottom -->
    <TestimonialsSection />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TestimonialsSection from '../components/TestimonialsSection.vue';

const formIframe = ref(null);
const { proxy } = getCurrentInstance();

onMounted(() => {
  // Get the current page URL
  const pageUrl = window.location.href;
  
  // When the iframe loads, find and auto-fill the PageUrl field
  formIframe.value.onload = () => {
    try {
      const iframeDocument = formIframe.value.contentDocument || formIframe.value.contentWindow.document;
      
      // Wait a bit for the form to fully initialize
      setTimeout(() => {
        // Look for the PageUrl input field in the iframe 
        // (the selector may need adjustment based on the actual form structure)
        const pageUrlInputs = iframeDocument.querySelectorAll('input');
        
        // Find the input with "Page URL" label or similar
        for (const input of pageUrlInputs) {
          const label = input.getAttribute('aria-label') || '';
          if (label.includes('Page') && label.includes('URL')) {
            // Fill the field with current URL
            input.value = pageUrl;
            
            // Hide the field (its parent div)
            const parentDiv = input.closest('div[role="listitem"]');
            if (parentDiv) {
              parentDiv.style.display = 'none';
            }
            
            break;
          }
        }
      }, 1000);
    } catch (error) {
      console.error('Error accessing iframe content:', error);
      // Cross-origin restrictions might prevent this from working
    }
  };

  proxy.$meta.update({
    title: 'Contact Real Yunnan - Start Planning Your Yunnan Trip',
    description: 'Get in touch with our Yunnan travel experts for free consultation and personalized travel advice. We respond within 24 hours.',
    image: '/images/contact.jpg',
    canonicalUrl: 'https://realyunnan.com/contact/',
    ogTitle: 'Contact Real Yunnan - Start Planning Your Yunnan Trip',
    ogDescription: 'Get in touch with our Yunnan travel experts for free consultation and personalized travel advice. We respond within 24 hours.',
    ogImage: '/images/contact.jpg',
    ogUrl: 'https://realyunnan.com/contact/'
  });
});
</script>

<style scoped>
.contact-page {
  min-height: calc(100vh - 70px);
}

.hero-section {
  min-height: 450px;
  background-image: url('/images/contact.jpg');
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-section h1 {
  color: white;
  font-size: 3.5rem;
  position: relative;
  z-index: 1;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.contact-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
}

.decorated-title {
  font-size: 2.5rem;
  color: var(--color-heading);
  margin-bottom: 2rem;
  display: inline-block;
}

/* Form Section */
.form-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 2rem;
}

.contact-description {
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--color-text);
  margin-bottom: 2rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
}

.contact-email {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.icon {
  font-size: 1.5rem;
  color: #30798b;
}

.contact-item a {
  color: #30798b;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.contact-item a:hover {
  color: #215863;
  transform: translateY(-2px);
}

.form-wrapper {
  overflow: hidden;
}

@media (max-width: 768px) {
  .hero-section {
    min-height: 300px;
  }

  .hero-section h1 {
    font-size: 2.5rem;
  }

  .contact-container {
    padding: 2rem 1rem;
  }
  
  .decorated-title {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
  
  .form-section {
    padding: 1.5rem;
  }

  .contact-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.2rem;
  }
  .contact-email {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .hero-section {
    min-height: 250px;
  }

  .hero-section h1 {
    font-size: 2rem;
  }
}
</style> 
